import {openDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {isString} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {catchUnhandledDataGridActions, DmsDataGrid, logError, Main} from '@dms/shared';

import type {ActionCallback} from 'features/eag-grid';

import {AbcXyzRowDetailDialog} from './components/AbcXyzRowDetailDialog';

const GRID_CODE = 'report-abc-xyz-analysis';
const ROW_DETAIL_TEST_ID = 'abcXyzRowDetail';

const actionCallback: ActionCallback = (params) => {
  const openDetail = () => {
    // A bulk action hands over every selected row; the detail only makes sense for a single one.
    // Reported rather than silently dropped — it would mean the grid definition wired this action
    // up as a bulk one.
    if (!isString(params.rowId)) {
      return logError(
        `Bulk invocation of single-row action ${params.actionKey} is not implemented`
      );
    }

    const rowId = params.rowId;

    // Keeps the context-menu action disabled while its dialog is open. Optional on purpose: the
    // plain row click passes a no-op lock and the legacy grid engine passes none.
    const actionLock = params.actionLock;
    actionLock?.lock();

    openDialog(<AbcXyzRowDetailDialog id={rowId} data-testid={ROW_DETAIL_TEST_ID} />, {
      // A per-row id so a double-click replaces the dialog instead of stacking a second one.
      id: `${ROW_DETAIL_TEST_ID}-${rowId}`,
      title: i18n.t('analytics.label.abcXyzAnalysis'),
      size: 'xLarge',
      scrollBehavior: 'inside',
      onCloseComplete: () => actionLock?.unlock(),
      'data-testid': ROW_DETAIL_TEST_ID,
    });
  };

  /**
   * The grid definition declares `redirectDetail`; the grid itself adds an "open in new tab"
   * context-menu item that dispatches the `_newTab` variant, and a dialog has no URL to open
   * separately, so both show the same detail.
   */
  match(params.actionKey)
    .with('redirectDetail', 'redirectDetail_newTab', openDetail)
    .otherwise(() => catchUnhandledDataGridActions(params.actionKey));
};

export function AbcXyzAnalysisReport() {
  return (
    <Main isFullHeight heading={i18n.t('analytics.label.abcXyzAnalysis')}>
      <DmsDataGrid
        gridCode={GRID_CODE}
        actionCallback={actionCallback}
        data-testid={testIds.warehouse.reportingAbcXyzAnalysis('datagrid')}
      />
    </Main>
  );
}
